import type { ApiEducationStatistics } from '@mfw/common';

import type {
  UpdateCourseMarketplaceConfigArgs,
  UnarchiveCourseByIdArgs,
  ArchiveCourseByIdArgs,
  FetchCourseByIdArgs,
  FetchCoursesArgs,
  CreateCourseArgs,
  EditCourseArgs,
} from './types/courses.types';
import type { ApiCourse } from '../schemas/courses.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  createCourseApiArgs,
  mapCourseFromApi,
} from './mappers/courses.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';

const base = (organizationId?: string) =>
  `/v2${organizationId ? `/organizations/${organizationId}` : ''}/education/courses`;

export const createCourse = async ({
  organizationId,
  ...args
}: CreateCourseArgs) => {
  const response = await post<ApiCourse>(
    base(organizationId),
    createCourseApiArgs(args),
  );
  if (!response?.data) return null;
  return mapCourseFromApi(response.data, args.locale);
};

export const editCourseById = async ({
  id,
  organizationId,
  ...args
}: EditCourseArgs) => {
  const response = await patch<ApiCourse>(
    `${base(organizationId)}/${id}`,
    createCourseApiArgs(args),
  );
  if (!response?.data) return null;
  return mapCourseFromApi(response.data, args.locale);
};

export const fetchCourseById = async ({
  id,
  locale,
  organizationId,
}: FetchCourseByIdArgs) => {
  const response = await get<BaseApiResponse<ApiCourse>>(
    `${base(organizationId)}/${id}`,
  );
  if (!response?.data) return null;
  return mapCourseFromApi(response.data, locale);
};

export const fetchCourseStatistics = async ({
  id,
  organizationId,
}: FetchCourseByIdArgs) => {
  const response = await get<BaseApiResponse<ApiEducationStatistics>>(
    `${base(organizationId)}/${id}/statistics`,
  );
  return response?.data ?? null;
};

export const fetchCourses = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
}: FetchCoursesArgs) => {
  const { search, status, ...restTableFilters } = tableFilters;
  const { authorIds, isHome, isMarketplace, sources, tags } =
    contentFilters || {};

  // active => isDeleted:false, archived => isDeleted:true, all => omit (both).
  const isDeleted =
    status === 'archived' ? true : status === 'all' ? undefined : false;

  const response = await postWithPagination<ApiCourse[]>(
    `${base(organizationId)}/list`,
    {
      ...restTableFilters,
      ...(search ? { search } : {}),
      ...(tags ? { tagIds: tags } : {}),
      // no source keeps the organization's own view; the backend narrows to
      // exactly the sources picked (the platform library is one of them)
      ...(sources?.length ? { sources } : {}),
      ...(isDeleted !== undefined ? { isDeleted } : {}),
      authorIds,
      isHome,
      isMarketplace,
    },
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response.data.map((course) => mapCourseFromApi(course, locale)),
  };
};

export const archiveCourseById = async ({
  id,
  organizationId,
}: ArchiveCourseByIdArgs) => {
  await destroy(`${base(organizationId)}/${id}`);
  return true;
};

export const unarchiveCourseById = async ({
  id,
  organizationId,
}: UnarchiveCourseByIdArgs) => {
  await patch(`${base(organizationId)}/${id}/restore`);
  return true;
};

export const updateCourseMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateCourseMarketplaceConfigArgs) => {
  await patch(`${base(organizationId)}/${id}/marketplace-config`, {
    configuration: {
      marketplace: { currency, enabled, price: enabled ? price : 0 },
    },
  });
};
